import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Plus, X, TrendingUp, Clock, Euro, BarChart3, CheckCircle2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useCasesAPI } from '../services/api';
import { useCasesDetailed as mockUseCases } from '../data/mockData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';

const ROIComparePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [selectedUseCases, setSelectedUseCases] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [useCasesDetailed, setUseCasesDetailed] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch use cases from API and merge with mockData for ROI config
  useEffect(() => {
    const fetchUseCases = async () => {
      try {
        const data = await useCasesAPI.getAll();
        
        // Title alias mapping for mismatches between DB and mockData
        const titleAliases = {
          'onboarding chatbot': 'automated onboarding journey',
        };
        
        // Merge each use case with mock ROI configuration
        const mergedUseCases = (data || []).map(uc => {
          const normalizedTitle = uc.title?.toLowerCase() || '';
          const aliasedTitle = titleAliases[normalizedTitle] || normalizedTitle;
          
          const mockMatch = mockUseCases.find(
            m => m.title?.toLowerCase() === normalizedTitle ||
                 m.title?.toLowerCase() === aliasedTitle
          );
          return {
            ...uc,
            roiVariables: mockMatch?.roiVariables || uc.roiVariables,
            roiCalculation: mockMatch?.roiCalculation || uc.roiCalculation,
            roiBreakdown: mockMatch?.roiBreakdown || uc.roiBreakdown,
          };
        });
        
        setUseCasesDetailed(mergedUseCases.filter((uc) => uc.roiVariables && uc.roiCalculation));
      } catch (error) {
        console.error('Failed to fetch use cases:', error);
        // Fallback to mockData if API fails
        setUseCasesDetailed(mockUseCases);
      } finally {
        setLoading(false);
      }
    };
    fetchUseCases();
  }, []);

  // Toggle use case selection
  const toggleUseCase = (useCase) => {
    setSelectedUseCases((prev) => {
      const exists = prev.find((uc) => uc.id === useCase.id);
      if (exists) {
        return prev.filter((uc) => uc.id !== useCase.id);
      }
      if (prev.length >= 4) {
        return prev; // Max 4 use cases
      }
      // Initialize with default values
      const defaults = {};
      useCase.roiVariables?.forEach((v) => {
        defaults[v.key] = v.default;
      });
      return [...prev, { ...useCase, variables: defaults }];
    });
  };

  // Remove a use case from comparison
  const removeUseCase = (id) => {
    setSelectedUseCases((prev) => prev.filter((uc) => uc.id !== id));
  };

  // Calculate ROI for a use case with its variables
  const calculateROI = (useCase) => {
    if (!useCase.roiCalculation || !useCase.variables) {
      return { savedHours: 0, savings: 0 };
    }
    try {
      return useCase.roiCalculation(useCase.variables);
    } catch (e) {
      return { savedHours: 0, savings: 0 };
    }
  };

  // Get implementation cost
  const getImplementationCost = (difficulty) => {
    switch (difficulty) {
      case 'low': return 10000;
      case 'medium': return 27500;
      case 'high': return 55000;
      default: return 20000;
    }
  };

  // Calculate totals - ALWAYS call useMemo (React hooks rule)
  const totals = useMemo(() => {
    let totalSavings = 0;
    let totalHours = 0;
    let totalCost = 0;

    selectedUseCases.forEach((uc) => {
      const results = calculateROI(uc);
      totalSavings += results.savings;
      totalHours += results.savedHours;
      totalCost += getImplementationCost(uc.difficulty);
    });

    const netROI = totalSavings - totalCost;
    const roiPercentage = totalCost > 0 ? Math.round(((totalSavings - totalCost) / totalCost) * 100) : 0;
    const paybackMonths = totalSavings > 0 ? Math.ceil((totalCost / totalSavings) * 12) : 0;

    return { totalSavings, totalHours, totalCost, netROI, roiPercentage, paybackMonths };
  }, [selectedUseCases]);

  // Sort use cases by ROI for recommendation - ALWAYS call useMemo (React hooks rule)
  const sortedByROI = useMemo(() => {
    return [...selectedUseCases]
      .map((uc) => {
        const results = calculateROI(uc);
        const cost = getImplementationCost(uc.difficulty);
        const roi = cost > 0 ? ((results.savings - cost) / cost) * 100 : 0;
        return { ...uc, results, cost, roi };
      })
      .sort((a, b) => b.roi - a.roi);
  }, [selectedUseCases]);

  const getDifficultyLabel = (difficulty) => {
    if (language === 'nl') {
      switch (difficulty) {
        case 'low': return 'Laag';
        case 'medium': return 'Gemiddeld';
        case 'high': return 'Hoog';
        default: return difficulty;
      }
    }
    switch (difficulty) {
      case 'low': return 'Low';
      case 'medium': return 'Medium';
      case 'high': return 'High';
      default: return difficulty;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
      <Header />
      
      <main className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <button
            onClick={() => navigate('/use-cases')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'nl' ? 'Terug naar Use Cases' : 'Back to Use Cases'}
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4 shadow-lg">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {language === 'nl' ? 'ROI Vergelijker' : 'ROI Comparer'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Vergelijk meerdere use cases naast elkaar om de beste investeringsbeslissingen te nemen'
                : 'Compare multiple use cases side by side to make the best investment decisions'}
            </p>
          </div>

          {/* Add Use Cases Button */}
          <div className="flex justify-center mb-8">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'nl' ? `Use Cases Toevoegen (${selectedUseCases.length}/4)` : `Add Use Cases (${selectedUseCases.length}/4)`}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{language === 'nl' ? 'Selecteer Use Cases om te Vergelijken' : 'Select Use Cases to Compare'}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 mt-4">
                  {useCasesDetailed.map((useCase) => {
                    const isSelected = selectedUseCases.some((uc) => uc.id === useCase.id);
                    const isDisabled = !isSelected && selectedUseCases.length >= 4;
                    
                    return (
                      <div
                        key={useCase.id}
                        className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : isDisabled 
                              ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                        onClick={() => !isDisabled && toggleUseCase(useCase)}
                      >
                        <Checkbox 
                          checked={isSelected}
                          disabled={isDisabled}
                          className="pointer-events-none"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{useCase.title}</p>
                          <p className="text-sm text-gray-500">{useCase.domainLabel} • {useCase.costRange}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(useCase.difficulty)}`}>
                          {getDifficultyLabel(useCase.difficulty)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end mt-4">
                  <Button onClick={() => setIsDialogOpen(false)}>
                    {language === 'nl' ? 'Klaar' : 'Done'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Empty State */}
          {selectedUseCases.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                <Calculator className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {language === 'nl' ? 'Geen Use Cases Geselecteerd' : 'No Use Cases Selected'}
              </h3>
              <p className="text-gray-500 mb-6">
                {language === 'nl' 
                  ? 'Voeg use cases toe om hun ROI naast elkaar te vergelijken'
                  : 'Add use cases to compare their ROI side by side'}
              </p>
              <Button onClick={() => setIsDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'nl' ? 'Use Cases Toevoegen' : 'Add Use Cases'}
              </Button>
            </div>
          )}

          {/* Comparison Grid */}
          {selectedUseCases.length > 0 && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
                  <CardContent className="p-6">
                    <p className="text-green-100 text-sm mb-1">{language === 'nl' ? 'Totale Jaarlijkse Besparing' : 'Total Annual Savings'}</p>
                    <p className="text-3xl font-bold">€{totals.totalSavings.toLocaleString()}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
                  <CardContent className="p-6">
                    <p className="text-blue-100 text-sm mb-1">{language === 'nl' ? 'Totale Uren Bespaard' : 'Total Hours Saved'}</p>
                    <p className="text-3xl font-bold">{totals.totalHours.toLocaleString()}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white border-0">
                  <CardContent className="p-6">
                    <p className="text-blue-100 text-sm mb-1">{language === 'nl' ? 'Totale Investering' : 'Total Investment'}</p>
                    <p className="text-3xl font-bold">€{totals.totalCost.toLocaleString()}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
                  <CardContent className="p-6">
                    <p className="text-purple-100 text-sm mb-1">{language === 'nl' ? 'Netto ROI Jaar 1' : 'Net ROI Year 1'}</p>
                    <p className="text-3xl font-bold">
                      {totals.netROI >= 0 ? '+' : ''}€{totals.netROI.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Use Case Cards */}
              <div className={`grid gap-6 mb-8 ${
                selectedUseCases.length === 1 ? 'grid-cols-1 max-w-lg mx-auto' :
                selectedUseCases.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                selectedUseCases.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              }`}>
                {selectedUseCases.map((useCase, index) => {
                  const results = calculateROI(useCase);
                  const cost = getImplementationCost(useCase.difficulty);
                  const roi = cost > 0 ? Math.round(((results.savings - cost) / cost) * 100) : 0;
                  const payback = results.savings > 0 ? Math.ceil((cost / results.savings) * 12) : 0;
                  const rank = sortedByROI.findIndex((uc) => uc.id === useCase.id) + 1;

                  return (
                    <Card key={useCase.id} className="relative overflow-hidden">
                      {/* Rank Badge */}
                      {selectedUseCases.length > 1 && (
                        <div className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-blue-400' : 'bg-gray-300'
                        }`}>
                          #{rank}
                        </div>
                      )}
                      
                      {/* Remove Button */}
                      <button
                        onClick={() => removeUseCase(useCase.id)}
                        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center transition-colors group"
                      >
                        <X className="h-4 w-4 text-gray-500 group-hover:text-red-500" />
                      </button>

                      <CardHeader className="pt-12 pb-2">
                        <CardTitle className="text-lg">{useCase.title}</CardTitle>
                        <p className="text-sm text-gray-500">{useCase.domainLabel}</p>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        {/* Main Metrics */}
                        <div className="bg-green-50 rounded-xl p-4">
                          <p className="text-sm text-gray-600 mb-1">{language === 'nl' ? 'Jaarlijkse Besparing' : 'Annual Savings'}</p>
                          <p className="text-2xl font-bold text-green-600">
                            €{results.savings.toLocaleString()}
                          </p>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">{language === 'nl' ? 'Uren bespaard' : 'Hours saved'}</span>
                            <span className="font-medium">{results.savedHours.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">{language === 'nl' ? 'Implementatie' : 'Implementation'}</span>
                            <span className="font-medium">€{cost.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">{language === 'nl' ? 'Terugverdientijd' : 'Payback period'}</span>
                            <span className="font-medium text-blue-600">
                              {language === 'nl' 
                                ? (payback <= 1 ? '< 1 maand' : payback > 36 ? '> 3 jaar' : `${payback} maanden`)
                                : (payback <= 1 ? '< 1 month' : payback > 36 ? '> 3 years' : `${payback} months`)}
                            </span>
                          </div>
                          <div className="flex justify-between pt-2 border-t">
                            <span className="text-gray-700 font-medium">{language === 'nl' ? 'ROI Jaar 1' : 'ROI Year 1'}</span>
                            <span className={`font-bold ${roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {roi >= 0 ? '+' : ''}{roi}%
                            </span>
                          </div>
                        </div>

                        {/* Difficulty Badge */}
                        <div className="flex justify-between items-center pt-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(useCase.difficulty)}`}>
                            {getDifficultyLabel(useCase.difficulty)} {language === 'nl' ? 'complexiteit' : 'complexity'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/use-cases/${useCase.id}/roi`)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            Details
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Recommendation Section */}
              {selectedUseCases.length > 1 && (
                <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-indigo-900">
                      <TrendingUp className="h-5 w-5" />
                      {language === 'nl' ? 'Aanbeveling' : 'Recommendation'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        {language === 'nl' 
                          ? 'Op basis van ROI-analyse raden wij de volgende implementatievolgorde aan:'
                          : 'Based on ROI analysis, we recommend the following implementation order:'}
                      </p>
                      <div className="space-y-2">
                        {sortedByROI.map((uc, index) => (
                          <div key={uc.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : index === 2 ? 'bg-purple-500' : 'bg-gray-400'
                            }`}>
                              {index + 1}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{uc.title}</p>
                              <p className="text-sm text-gray-500">
                                ROI: {Math.round(uc.roi)}% • {language === 'nl' ? 'Terugverdientijd' : 'Payback'}: {
                                  uc.results.savings > 0 
                                    ? Math.ceil((uc.cost / uc.results.savings) * 12) <= 1 
                                      ? (language === 'nl' ? '< 1 maand' : '< 1 month')
                                      : `${Math.ceil((uc.cost / uc.results.savings) * 12)} ${language === 'nl' ? 'maanden' : 'months'}`
                                    : 'N/A'
                                }
                              </p>
                            </div>
                            <CheckCircle2 className={`h-5 w-5 ${index === 0 ? 'text-green-500' : 'text-gray-300'}`} />
                          </div>
                        ))}
                      </div>
                      
                      {/* 3-Year Projection */}
                      <div className="mt-6 p-4 bg-white rounded-xl">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Euro className="h-4 w-4 text-green-500" />
                          {language === 'nl' ? '3-Jaar Gecombineerde Projectie' : '3-Year Combined Projection'}
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          {[1, 2, 3].map((year) => {
                            const cumulative = totals.totalSavings * year - totals.totalCost;
                            return (
                              <div key={year} className="text-center p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-500 mb-1">{language === 'nl' ? 'Jaar' : 'Year'} {year}</p>
                                <p className={`text-xl font-bold ${cumulative >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {cumulative >= 0 ? '+' : ''}€{cumulative.toLocaleString()}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CTA Section */}
              <div className="mt-8 text-center">
                <Card className="bg-gradient-to-r from-blue-500 to-blue-700 text-white border-0">
                  <CardContent className="py-8">
                    <h3 className="text-2xl font-bold mb-2">
                      {language === 'nl' ? 'Klaar om te Starten?' : 'Ready to Start?'}
                    </h3>
                    <p className="text-blue-100 mb-6 max-w-xl mx-auto">
                      {language === 'nl' 
                        ? 'Boek een workshop en bouw uw eerste AI use case met expert begeleiding. Wij helpen u van concept naar werkend prototype in één dag.'
                        : 'Book a workshop and build your first AI use case with expert guidance. We help you from concept to working prototype in one day.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button 
                        onClick={() => navigate('/quickscan/start')}
                        className="bg-white text-blue-600 hover:bg-blue-50"
                      >
                        {language === 'nl' ? 'Start Quickscan' : 'Start Quickscan'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-white text-white hover:bg-white/10"
                        onClick={() => {
                          if (sortedByROI.length > 0) {
                            navigate(`/use-cases/${sortedByROI[0].id}/workshop`);
                          }
                        }}
                      >
                        {language === 'nl' ? 'Boek Workshop' : 'Book Workshop'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ROIComparePage;
